import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { getFormValues, change } from "redux-form";
import { noop } from "rhombus-react";

import PlacementSelect from "social/PlacementSelect";
import PlacementTag from "social/PlacementTag";
import { PAC_FORM_TYPES } from "social/constants";
import { PAC_FIELDS_BY_AD_TYPE } from "social/constants/placements";

import { SOCIAL_AD_TYPE_LIST } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";

import "./PlacementSelector.css";

function initialPlacementValue(adType, values, placement){
	const fieldNames = PAC_FIELDS_BY_AD_TYPE[adType]?.[placement.name] || [];
	const initialValues = fieldNames.reduce((prev, fieldName) => {
		const defaultValue = values[fieldName];
		if(defaultValue && typeof(defaultValue) === "object"){
			prev[fieldName] = {
				isDynamic: defaultValue.isDynamic,
			};
		}

		return prev;
	}, {
		_isNew: true,
		type: {
			id: placement.id,
			name: placement.name,
			description: placement.description,
		},
		status: "active",
	});

	return initialValues;
}

export default function PlacementSelector(props) {
	const {
		adType,
		showPristineErrors,
		isViewOnly,
		setActivePlacement,
		selectProps,
	} = props;

	const dispatch = useDispatch();
	const [selectedPlacements, setSelectedPlacements] = useState([]); // Placements currently in the select input, but not yet activated

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType]);
	const { placements = {} } = values || {};
	const { activePlacementsSet, inactivePlacementsSet, tags } = useMemo(() => {
		/*
			Creatives may have placements already attached in either an 'active' or 'disabled' state. To avoid redundant
			searches, we memoize two Sets based off of the placements data on the creative.
		 */
		const activePlacementsSet = new Set();
		const inactivePlacementsSet = new Set();
		const activeTags = [];

		Object.values(placements).forEach((placement) => {
			const { name, id } = placement.type;

			if (placement.status === 'active') {
				activePlacementsSet.add(name);
				activeTags.push({ name, id });
			} else {
				inactivePlacementsSet.add(name);
			}
		});

		const tags = activeTags
			.sort((tag1, tag2) => tag1.id - tag2.id)
			.map(({ name }) => (
				<PlacementTag
					key={name}
					pacType={name}
					adType={adType}
					showPristineErrors={showPristineErrors}
					canRemove={!isViewOnly}
					setActivePlacement={setActivePlacement}
				/>
			));

		return { activePlacementsSet, inactivePlacementsSet, tags };
	}, [placements]);

	function filterPlacementOptions(option, input = "") {
		return option.data.name !== PAC_FORM_TYPES.DEFAULT && !activePlacementsSet.has(option.data.name) && option.label?.toLowerCase().includes(input.toLowerCase());
	}

	function onAddPlacements() {
		selectedPlacements.forEach((val) => {
			if (inactivePlacementsSet.has(val.name)) {
				activatePlacement(val.name);
			} else {
				addPlacement(val);
			}
		});

		setSelectedPlacements([]);
		dispatch(change(SOCIAL_WIZARD, `${adType}._select-aspect-ratios`, [])); // Empty out the select field
	}

	function addPlacement(placement) {
		const fieldValues = initialPlacementValue(adType, values, placement);

		dispatch(change(SOCIAL_WIZARD, `${adType}.placements.${placement.name}`, fieldValues));
	}

	function activatePlacement(name) {
		dispatch(change(SOCIAL_WIZARD, `${adType}.placements.${name}.status`, "active"));
	}

	return (
		<div className="pac-selector">
			<div className="pac-select-container">
				<PlacementSelect
					onAddPlacements={onAddPlacements}
					onChange={setSelectedPlacements}
					filterOption={filterPlacementOptions}
					value={selectedPlacements}
					isDisabled={isViewOnly}
					showPristineErrors={showPristineErrors}
					adType={adType}
					selectProps={selectProps}
				/>
			</div>
			<div className="pac-fields-container">
				<PlacementTag
					pacType={PAC_FORM_TYPES.DEFAULT}
					adType={adType}
					showPristineErrors={showPristineErrors}
					setActivePlacement={setActivePlacement}
				/>
				{tags}
			</div>
		</div>
	);
}

PlacementSelector.propTypes = {
	adType: PropTypes.oneOf(SOCIAL_AD_TYPE_LIST).isRequired,
	onTagClick: PropTypes.func,
	setActivePlacement: PropTypes.func,
};
PlacementSelector.defaultProps = {
	onTagClick: noop,
};
